/* eslint-disable max-lines-per-function */
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { BHDialogFreeFormMails } from '@adornis/bh-mails/client/bh-dialog-free-form-mails.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import {
  subscribeLecturerDataview,
  subscribeLecturerDataviewCount,
} from '@adornis/buchungsheld-db/api/lecturer-data.js';
import { promoteUserToLecturer } from '@adornis/buchungsheld-db/api/teaching.js';
import { subscribeUserDataview } from '@adornis/buchungsheld-db/api/user-data.js';
import { sendFreeFormMail } from '@adornis/buchungsheld-db/api/users.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { InstitutionMailTemplate, MailTemplateType } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { dataviewEmptyState, dataviewLoadingState } from '@adornis/buchungsheld-db/global-helpers.js';
import { LecturerDataview } from '@adornis/buchungsheld-db/use-case-entities/LecturerDataview.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { consume } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, delay, filter, firstValueFrom, map, merge, Subject, switchMap } from 'rxjs';
import { BHPromptUserPicker } from '../../prompts/bh-prompt-user-picker.js';
import { entityContextTokenProvider } from '../../util.js';

/**
 * @element bh-page-admin-einrichtung-lecturers
 */
@customElement('bh-page-admin-einrichtung-lecturers')
export class BHPageAdminEinrichtungLecturers extends BuchungsheldLitElement {
  @consume({
    context: entityContextTokenProvider.getContext(AdminInstitutionViewSetting),
    subscribe: true,
  })
  @state()
  protected readonly viewSettings?: {
    $instance: AdminInstitutionViewSetting;
  };
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() filter = new RXController(
    this,
    combineLatest([this.institutionID.observable]).pipe(
      filter(([institutionID]) => !!institutionID),
      delay(2000), // FIXME: this is a hack to wait for the viewSettings to load. This would not be necessary with functional components
      map(([institutionID]) => {
        const filter = new BHFilter({
          institutionID,
          searchFields: ['user.firstName', 'user.lastName', 'user.email'],
          search: '',
        });

        const savedAspects = this.viewSettings?.$instance?.institutionDetailsLecturersFilterV1?.aspects;
        const savedSortFields = this.viewSettings?.$instance?.institutionDetailsLecturersFilterV1?.sortingFields;
        const savedSortDirections =
          this.viewSettings?.$instance?.institutionDetailsLecturersFilterV1?.sortingDirections;
        if (savedAspects && savedAspects.length > 0) filter.aspects = savedAspects as any;
        if (savedSortFields && savedSortFields.length > 0) filter.sortingFields = savedSortFields as any;
        if (savedSortDirections && savedSortDirections.length > 0)
          filter.sortingDirections = savedSortDirections as any;

        return filter;
      }, new BHFilter({})),
    ),
  );

  filterOptions$ = new Subject<BHFilter>();

  saveFilter = async (filter: any) => {
    if (!this.viewSettings?.$instance || !this.institutionID.value) return;
    const instance = this.viewSettings.$instance;
    instance.institutionDetailsLecturersFilterV1 = filter;
    await instance.save();
  };

  @state() memberCount = new RXController(
    this,

    merge(this.filter.observable, this.filterOptions$).pipe(
      filter(Boolean),
      switchMap(filter => subscribeLecturerDataviewCount(filter)()),
    ),
  );

  @state() private _selectedUsers = new RXController<string[] | 'all'>(this, []);

  override render() {
    if (this.institutionID.isLoading || !this.institutionID.value || !this.filter.value)
      return html`<x-loading-state></x-loading-state>`;

    const { dataViewOptions, filterOptions } = buildFullOptions({
      class: LecturerDataview,
      operation: subscribeLecturerDataview,
      isEntryClickable: true,
      isSortable: true,
      isColumnSelectable: true,
      isColumnResizable: true,
      view: new View({ filter: this.filter.value }),
      onViewChanged: view => {
        // TODO: save filter in onSortUpdate Event or somehow ignore dataview update
        // this.saveFilter(view.filter);
      },
      loader: () => dataviewLoadingState(this),
      emptyState: () => dataviewEmptyState({}),
      customColumns: [
        {
          columnName: '',
          fieldName: 'user._id',
          columnWidth: 'max-content',
          renderItem: _instance => html`
            <tr-checkbox
              outlined
              boolean-only
              enableicon="adjust"
              disableicon
              @click=${(e: MouseEvent) => e.stopPropagation()}
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (this._selectedUsers.value === 'all') return this._selectedUsers.next([_instance.user._id]);
                if (e.detail.value === true && !this._selectedUsers.value.includes(_instance.user._id))
                  return this._selectedUsers.next((this._selectedUsers.value as string[]).concat([_instance.user._id]));
                if (e.detail.value === false && this._selectedUsers.value.includes(_instance.user._id)) {
                  const index = this._selectedUsers.value.indexOf(_instance.user._id);
                  if (index !== -1)
                    this._selectedUsers.next((this._selectedUsers.value as string[]).toSpliced(index, 1));
                  return;
                }
              }}
              .value=${this._selectedUsers.value === 'all' || this._selectedUsers.value.includes(_instance.user._id)}
            ></tr-checkbox>
          `,
          renderHeader: () => html`
            <tr-checkbox
              outlined
              boolean-only
              enableicon="adjust"
              disableicon
              @click=${(e: MouseEvent) => e.stopPropagation()}
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (e.detail.value === true) this._selectedUsers.next('all');
                if (e.detail.value === false) this._selectedUsers.next([]);
              }}
              .value=${this._selectedUsers.value === 'all'}
            ></tr-checkbox>
          `,
        },
        { fieldName: 'user.firstName' },
        { fieldName: 'user.lastName' },
        { fieldName: 'user.email' },
        {
          columnName: this._translation.translate('termin', 'plural'),
          fieldName: 'products.name',
          columnWidth: '3fr',
          renderItem: instance => {
            return html`
              <tr-flex horizontal wrap space="sm">
                ${instance.products.map(product => {
                  return html` <tr-text> ${product.name} </tr-text> `;
                })}
              </tr-flex>
            `;
          },
        },

        {
          columnName: '',
          customID: '_options',
          columnWidth: 'max-content',
          selectionSet: LecturerDataview.neededGqlFields,
          isAlwaysVisible: true,
          renderItem: instance => {
            return html`
              <x-flex horizontal space="sm">
                <tr-button-small
                  icon="edit"
                  mode="outline"
                  .color=${this.colors.tone.onyx}
                  .action=${async e => {
                    goTo(`/admin/institution-details/${this.institutionID.value}/lecturer/${instance.user._id}`);
                  }}
                >
                  <tr-tooltip .text=${html` <x-text> ${this._translation.translate('user', 'details')} </x-text> `}>
                  </tr-tooltip>
                </tr-button-small>

                <tr-button-small
                  icon="email"
                  mode="outline"
                  .action=${() => {
                    window.location.href = `mailto:${instance.user.email}`;
                  }}
                >
                  <tr-tooltip
                    .text=${html` <x-text>${this._translation.translate('user', 'write')}</x-text> `}
                  ></tr-tooltip>
                </tr-button-small>
              </x-flex>
            `;
          },
        },
      ] as Array<ColumnDefinition<typeof LecturerDataview>>,
    });

    return html`
      <tr-flex space="md">
        <bh-breadcrumb
          .items=${[{ content: this._translation.translate('SIDEBAR', 'lecturers'), active: true }]}
        ></bh-breadcrumb>

        <x-flex horizontal space-between>
          ${this._translation.translate('SIDEBAR', 'lecturers')}: ${this.memberCount.value}

          <x-button icon="add" @click=${async () => this.addLecturer()}>
            ${this._translation.translate('workshop', 'addLecturer')}
          </x-button>
        </x-flex>

        <x-filter-editor
          .options=${filterOptions}
          @filter-changed=${e => this.filterOptions$.next(e.detail.value)}
          @filter-apply=${e => this.saveFilter(e.detail)}
          @filter-reset=${() => this.saveFilter(null)}
        ></x-filter-editor>

        ${this._selectedUsers.value.length >= 1 || this._selectedUsers.value === 'all'
          ? html`
              <x-flex
                horizontal
                space-between
                ${acss({
                  position: 'sticky',
                  top: `${this.spacing.xl + this.spacing.md}px`,
                  padding: this.spacing.xs,
                  border: `2px solid ${this.colors.secondary.shade(300)}`,
                  borderRadius: this.spacing.sm,
                  background: this.colors.tone.snowWhite!.opacity(0.92),
                  margin: `0 -${this.spacing.xs + 2}px`,
                  zIndex: '9',

                  'tr-icon': {
                    marginLeft: `-${this.spacing.xxs}`,
                  },
                  '[info]': {
                    color: this.colors.secondary,
                  },
                })}
              >
                <x-flex horizontal start crossaxis-center space="xs">
                  <tr-button-small icon="person" .color=${this.colors.secondary.shade(300)} .action=${async () => null}>
                    <tr-text bold ${css({ marginLeft: `-${this.spacing.xs}` })}
                      >${this._selectedUsers.value === 'all'
                        ? this.memberCount.value
                        : this._selectedUsers.value.length}x</tr-text
                    >
                  </tr-button-small>

                  <tr-text bold info> markierte Anmeldungen </tr-text>
                </x-flex>

                <x-flex horizontal start crossaxis-center space="xs">
                  <tr-button-small
                    icon="mail"
                    .color=${this.colors.secondary}
                    .action=${async e => {
                      const mailAddresses = Array.isArray(this._selectedUsers.value)
                        ? await getByID(
                            User,
                            this._selectedUsers.value,
                          )({ email: 1 }).then(users => users.map(user => user.email))
                        : await firstValueFrom(
                            subscribeUserDataview(
                              0,
                              10000,
                              dataViewOptions.view.filter || new BHFilter({}),
                            )({ user: { email: 1 } }),
                          ).then(userObjs => userObjs.map(userObj => userObj.user.email));
                      if (mailAddresses.length === 10000) {
                        TrDialog.alert('Zu viele Nutzer gleichzeitig selektiert.');
                        throw new Error('Cannot select more than 10000 users at the same time');
                      }

                      await BHDialogFreeFormMails.showPopup({
                        props: {
                          institutionID: this.institutionID.value,
                          mailAddresses,
                          mailTemplateType: MailTemplateType.FREE_FORM,
                          mailTemplateConstructor: (doc: PartialEntityData<InstitutionMailTemplate>) =>
                            new InstitutionMailTemplate({ ...doc, institutionID: this.institutionID.value }),
                          mailSendOperation: sendFreeFormMail,
                        },
                      });
                    }}
                  >
                    <x-text> Mail senden </x-text>
                  </tr-button-small>
                  <tr-button-small
                    .color=${this.colors.secondary.shade(300)}
                    icon="clear"
                    .action=${async () => this._selectedUsers.next([])}
                  ></tr-button-small>
                </x-flex>
              </x-flex>
            `
          : nothing}
        <x-data-view .options=${dataViewOptions}></x-data-view>
      </tr-flex>
    `;
  }
  private async addLecturer() {
    if (!this.institutionID.value) return TrDialog.alert('fatal: missing institution ID');
    try {
      const newLecturer = await BHPromptUserPicker.showPopup<Maybe<User>>({
        props: {
          institutionID: this.institutionID.value,
          userTypeForNewlyCreatedUser: 'lecturer',
        },
      });
      if (!newLecturer) return;
      await promoteUserToLecturer(newLecturer._id, this.institutionID.value)();
    } catch (e: unknown) {
      if (e instanceof Error) {
        TrDialog.alert(`Error: ${(e as Error).message}`);
      } else {
        TrDialog.alert(this._translation.translate('err', 'unexpected') as Renderable);
      }
    }
    this.requestUpdate();
    // requestUpdate() doesn't suffice, so we trigger a rerender using this:
    this.filterOptions$.next(this.filter.value ?? new BHFilter({}));
  }
}
